import type { ClientMessage, ServerMessage } from "../gen/livesoup/v1/realtime_pb";
import { client, decodeServer, encodeClient } from "./codec";

export type WsStatus = "connecting" | "online" | "offline";

export function nextBackoff(prevMs: number): number {
  if (prevMs <= 0) return 1000;
  return Math.min(prevMs * 2, 30000);
}

const PING_EVERY = 10_000;
const PONG_TIMEOUT = 30_000;

export class WsClient {
  private ws: WebSocket | null = null;
  private backoff = 0;
  private closedByUser = false;
  private handlers = new Set<(m: ServerMessage) => void>();
  private statusHandlers = new Set<(s: WsStatus) => void>();
  private pingTimer: ReturnType<typeof setInterval> | undefined;
  private lastPong = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | undefined;

  constructor(private readonly url: string) {}

  on(h: (m: ServerMessage) => void): () => void { this.handlers.add(h); return () => this.handlers.delete(h); }
  onStatus(h: (s: WsStatus) => void): () => void { this.statusHandlers.add(h); return () => this.statusHandlers.delete(h); }
  private setStatus(s: WsStatus): void { for (const h of this.statusHandlers) h(s); }

  connect(): void {
    this.closedByUser = false;
    this.setStatus("connecting");
    const ws = new WebSocket(this.url);
    ws.binaryType = "arraybuffer";
    this.ws = ws;
    ws.onopen = () => {
      this.backoff = 0;
      this.lastPong = performance.now();
      this.setStatus("online");
      clearInterval(this.pingTimer);
      this.pingTimer = setInterval(() => {
        if (performance.now() - this.lastPong > PONG_TIMEOUT) { ws.close(4000, "pong timeout"); return; }
        this.send(client({ case: "ping", value: { clientTimeMs: BigInt(Date.now()) } }));
      }, PING_EVERY);
    };
    ws.onmessage = (ev) => {
      const msg = decodeServer(new Uint8Array(ev.data as ArrayBuffer));
      if (msg.msg.case === "pong") this.lastPong = performance.now();
      for (const h of this.handlers) h(msg);
    };
    ws.onclose = () => {
      clearInterval(this.pingTimer);
      this.ws = null;
      this.setStatus("offline");
      if (this.closedByUser) return;
      this.backoff = nextBackoff(this.backoff);
      this.reconnectTimer = setTimeout(() => this.connect(), this.backoff);
    };
    ws.onerror = () => { /* onclose follows */ };
  }

  send(msg: ClientMessage): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(encodeClient(msg));
  }

  close(): void {
    this.closedByUser = true;
    clearTimeout(this.reconnectTimer);
    clearInterval(this.pingTimer);
    this.ws?.close(1000, "bye");
  }
}
